import { useCallback, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'

import { resolveTabLocation } from '@/config/routeManifest'
import { announce } from '@/components/panel/tabBar/ShelfAnnouncer'
import { useAppNavigate } from '@/hooks/useAppNavigate'
import { useAppStore, type WorkspaceTab } from '@/stores/app'
import { useTabDrafts } from '@/stores/tabDrafts'
import { usePendingClose, type CloseOutcome } from '@/stores/closeOwners'

interface UseAppTabs {
  tabs: WorkspaceTab[]
  /** Derived from the URL — the open tab whose id matches the current location. */
  activeTab: WorkspaceTab | null
  /** Navigates to the route (carrying shell keys). The bridge updates the store. */
  openTab: (route: string) => void
  /**
   * User-initiated close. A clean tab closes immediately; a dirty one raises
   * the save/discard/cancel dialog (activating the tab first when it is not
   * the current one) and resolves once the user decides. The promise settles
   * with what actually happened, so callers can gate follow-up work such as
   * moving focus.
   *
   * "Dirty" covers editors that persist a draft through `useEditorDraft`
   * (documents, meeting notes) plus the composite tabs whose cards register
   * with the editor registry. The cards that do not register yet are still
   * invisible to this gate and carry a TODO at the fix site.
   */
  requestClose: (id: string) => Promise<CloseOutcome>
  /**
   * The close itself, with no dirty check. Only CloseTabDialog calls this —
   * it is the tail of a `requestClose` the user has already decided.
   */
  performClose: (id: string) => void
  /**
   * Forced close — no prompt, no dirty check. For cases where keeping the tab
   * is not an option (e.g. a route guard denied access), so there is nothing
   * for the user to decide. `reason` is logged and the close is announced.
   */
  evictTab: (id: string, reason: string) => void
  /** Updates the tab's title. No-op if the id isn't open or the title is unchanged. */
  renameTab: (id: string, title: string) => void
}

/**
 * Public API for app-shell tabs. Components and the AI agent should use this
 * hook instead of reading the store directly — keeps the agentic surface
 * small and stable. The bridge (`AppShellEffects`) owns URL → store sync;
 * this hook exposes the explicit user actions, including cross-store
 * orchestration for close (drafts + navigation + the unsaved-work dialog).
 */
export function useAppTabs(): UseAppTabs {
  const { toTask } = useAppNavigate()
  const location = useLocation()

  const tabs = useAppStore((s) => s.tabs.items)

  const activeTab = useMemo<WorkspaceTab | null>(() => {
    const resolved = resolveTabLocation(location)
    if (!resolved) return null
    return tabs.find((t) => t.id === resolved.id) ?? null
  }, [tabs, location])

  const activeTabId = activeTab?.id ?? null

  // Read at call time, not capture time: a dirty close runs renders later than
  // the request that started it (the tab is activated first), so a captured
  // value would still say "not active" and skip the navigation.
  const activeTabIdRef = useRef(activeTabId)
  activeTabIdRef.current = activeTabId

  /**
   * The close itself: settle any parked request for this tab, drop the draft,
   * drop the tab, and — when it was the active one — navigate to the surviving
   * left neighbour (or Home). Side effects (markClosing + clearDraft) run
   * before the slice mutates so the editor's persistOnUnmount path sees
   * isTabClosing=true and skips re-persistence.
   */
  const performClose = useCallback(
    (id: string) => {
      // Every route out of a tab lands here, so this is the one place that can
      // guarantee a parked dialog is never stranded — an eviction or a close
      // that races the prompt settles it instead of jamming later closes.
      const parked = usePendingClose.getState()
      if (parked.pending?.tabId === id) parked.settle('closed')
      const title = useAppStore
        .getState()
        .tabs.items.find((t) => t.id === id)?.title
      const closedWasActive = activeTabIdRef.current === id
      const drafts = useTabDrafts.getState()
      drafts.markClosing(id)
      drafts.clearDraft(id)
      const { removed, neighborHref } = useAppStore.getState().closeTab(id)
      if (removed && closedWasActive) {
        // Active-tab close: leave the marker in place so the editor's
        // persistOnUnmount cleanup reads isTabClosing=true. AppShellEffects
        // prunes it after navigation.
        // replace follows the plan's history decision: closing the current
        // task replaces its entry only. Deeper history entries still reopen
        // closed tabs by design (no tombstones) — Back past the close point
        // restores the tab.
        toTask(neighborHref ?? '/', { replace: true })
      } else {
        // Non-active close: the tab is either already unmounted (the default
        // policy) or, on a keepAlive route, still mounted under <Activity
        // mode="hidden"> with its persist cleanup long since run. Either way
        // there is no unmount race left to guard.
        // Prune now — otherwise the marker leaks until the user navigates
        // (which doesn't happen) and would later make a freshly-mounted editor
        // skip its initial draft write on reopen.
        drafts.unmarkClosing(id)
      }
      if (removed) announce(`${title} closed`)
    },
    [toTask]
  )

  const requestClose = useCallback(
    (id: string): Promise<CloseOutcome> => {
      const tab = useAppStore.getState().tabs.items.find((t) => t.id === id)
      // An id that is not open has nothing to prompt about, so it takes the
      // clean path too — closeTab is a no-op and any orphan draft is dropped.
      if (!tab || !useTabDrafts.getState().isDirty(id)) {
        performClose(id)
        return Promise.resolve<CloseOutcome>('closed')
      }
      // One dialog at a time. A second request (double-click, or a close from
      // the overflow menu while the dialog is up) is dropped rather than
      // overwriting the pending entry, which would strand its promise.
      if (usePendingClose.getState().pending) {
        return Promise.resolve<CloseOutcome>('cancelled')
      }
      return new Promise<CloseOutcome>((resolve) => {
        usePendingClose.getState().open({ tabId: id, resolve })
        // Activate first, then prompt: the dialog talks about this tab's
        // unsaved work, so the user must be looking at it. CloseTabDialog
        // waits for the tab to become active before it renders.
        if (activeTabIdRef.current !== id) toTask(tab.href)
      })
    },
    [performClose, toTask]
  )

  const evictTab = useCallback(
    (id: string, reason: string) => {
      const tab = useAppStore.getState().tabs.items.find((t) => t.id === id)
      if (!tab) return
      console.warn(`Tab "${tab.title}" closed: ${reason}`)
      // performClose announces the close and settles a dialog parked on this
      // tab, so an eviction mid-prompt cannot leave the request hanging.
      performClose(id)
    },
    [performClose]
  )

  const renameTab = useCallback((id: string, title: string) => {
    useAppStore.getState().renameTab(id, title)
  }, [])

  return {
    tabs,
    activeTab,
    openTab: toTask,
    requestClose,
    performClose,
    evictTab,
    renameTab,
  }
}
